import { useMutation } from '@tanstack/react-query';
import { Col, Flex, message, Row } from 'antd';
import CNPlanApis from 'apis/CNPlanApis';
import ButtonExport from 'components/Button/ButtonExport';
import { Collapse } from 'components/Collapse/Collapse';
import { useTitle } from 'contexts/TitleProvider';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import path from 'routes/Path';
import { exportExcelWithManySheet } from 'utils/constants/exportExcel';
import { generateUUID } from 'utils/constants/functions';
import TableCNFollowPlanDetail from './TableCNFollowPlanDetail';

const processData = (data: any, key: string) => {
  const { revenue_plant: revenuePlant, ...restInvoice } = data[key];

  const res = [...revenuePlant, restInvoice];

  return res.reduce((acc, item) => {
    const {
      team_name,
      total_year_revenue,
      total_revenue_year_actual,
      total_year_revenue_actual,
      total_year_percent,
      ...rest
    } = item;
    const arr: any[] = [
      {
        plant: team_name ?? '-',
        title: 'Kế hoạch',
        id: generateUUID(),
        total: total_year_revenue,
      },
      {
        plant: team_name ?? '-',
        title: 'Doanh thu thực tế',
        id: generateUUID(),
        total: total_revenue_year_actual ?? total_year_revenue_actual,
      },
      {
        plant: team_name ?? '-',
        title: '%',
        id: generateUUID(),
        total: total_year_percent,
      },
    ];

    const keys = Object.keys(rest);

    keys.forEach((key) => {
      if (key.startsWith('month_revenue_actual_'))
        arr[1][key.replace('month_revenue_actual_', '')] = item[key];
      if (key.startsWith('total_month_revenue_actual_'))
        arr[1][key.replace('total_month_revenue_actual_', '')] = item[key];
      else if (key.startsWith('month_revenue_'))
        arr[0][key.replace('month_revenue_', '')] = item[key];
      else if (key.startsWith('total_month_revenue_'))
        arr[0][key.replace('total_month_revenue_', '')] = item[key];
      else if (key.startsWith('total_month_percent_'))
        arr[2][key.replace('total_month_percent_', '')] = item[key];
      else if (key.startsWith('total_'))
        arr[2][key.replace('total_', '')] = item[key];
      else if (key.startsWith('month_percent_'))
        arr[2][key.replace('month_percent_', '')] = item[key];
    });

    return [...acc, ...arr];
  }, []);
};

const getItems = ({
  key,
  children,
  label,
  extra,
}: {
  key: string;
  label: string;
  children: ReactElement;
  extra?: ReactElement;
}) => {
  return [
    {
      key,
      label,
      children,
      extra,
    },
  ];
};

interface TableRef {
  getData: () => any;
}

const refactorData = (data: any, teamSaleId: string) => {
  if (!data) return [];

  if (teamSaleId === '5000' || !teamSaleId) return data;

  const length = data.length;

  return [...data].slice(0, length - 3);
};

const CNFollowPlanDetail = () => {
  const location = useLocation();
  const record = location.state;
  const processRevenueTableRef = useRef<TableRef>(null);
  const profitRevenueTableRef = useRef<TableRef | null>(null);
  const { setTitle, setBreadcrumb } = useTitle();
  const [processRevenue, setProcessRevenue] = useState<any>();
  const [profitRevenue, setProfitRevenue] = useState<any>();
  const [activeKeys, setActiveKeys] = useState<string[]>(['1', '2']);

  const processRevenueProcess = useMemo(
    () => refactorData(processRevenue, record.team_sales_id),
    [processRevenue, record.team_sales_id],
  );
  const profitRevenueProcess = useMemo(
    () => refactorData(profitRevenue, record.team_sales_id),
    [profitRevenue, record.team_sales_id],
  );

  const {
    mutate: getTrackRevenueTeamSalesDetail,
    isPending: isGetTrackRevenueTeamSalesDetail,
  } = useMutation({
    mutationFn: CNPlanApis.getTrackRevenueTeamSalesDetail,
    onSuccess: (res) => {
      const data = res.data.getTrackRevenueDetailTeamSale;

      setProcessRevenue(processData(data, 'process_revenue'));
      setProfitRevenue(processData(data, 'profit_revenue'));
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleClickDownload = async () => {
    // Mở tất cả các panel
    setActiveKeys(['1', '2']);

    // Đợi một chút để các component được render
    await new Promise((resolve) => setTimeout(resolve, 100));

    if (!processRevenueTableRef.current || !profitRevenueTableRef.current) {
      message.warning('Đang tải dữ liệu, vui lòng thử lại');
      return;
    }

    const processRevenue = processRevenueTableRef.current.getData();
    const profitRevenue = profitRevenueTableRef.current.getData();

    exportExcelWithManySheet(
      [
        {
          data: processRevenue.datas,
          headerRow: processRevenue.headerRow,
          sheetName: 'Doanh thu công',
          isShowSTT: false,
        },
        {
          data: profitRevenue.datas,
          headerRow: profitRevenue.headerRow,
          sheetName: 'Lãi gộp',
          isShowSTT: false,
        },
      ],
      '[CN] Theo dõi KH',
    );

    // Sau khi export xong, đóng lại các panel (tùy chọn)
    setActiveKeys([]);
  };

  useEffect(() => {
    console.log(record);

    const title = record?.team_sub_id
      ? `${record.year} - [${record.team_sub_id}] ${record.team_sales_name}`
      : `${record.year} - Theo dõi kế hoạch đội`;

    setTitle(title);
    setBreadcrumb([
      { title: 'Vận hành' },
      { title: '[CN] Theo dõi KH', href: path.CN_FOLLOW_PLAN },
      {
        title: title,
      },
    ]);
  }, [record, setBreadcrumb, setTitle]);

  useEffect(() => {
    if (record.year)
      getTrackRevenueTeamSalesDetail({
        plantId: record.plant_id,
        year: record.year,
        teamSalesId: record.team_sales_id,
      });
  }, [
    getTrackRevenueTeamSalesDetail,
    record.plant_id,
    record.team_sales_id,
    record.year,
  ]);

  return (
    <Flex gap={12} vertical>
      <Row gutter={[8, 16]} className="mb-[17px]" justify="end">
        <Col>
          <ButtonExport onClick={handleClickDownload} />
        </Col>
      </Row>
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        items={getItems({
          key: '1',
          label: 'Doanh thu công',
          children: (
            <TableCNFollowPlanDetail
              year={record.year}
              data={processRevenueProcess}
              loading={isGetTrackRevenueTeamSalesDetail}
              title={`${record.year} - Kế hoạch hội sở`}
              ref={processRevenueTableRef}
            />
          ),
          extra: <p>Đơn vị: triệu VND</p>,
        })}
      />
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        items={getItems({
          key: '2',
          label: 'Lãi gộp',
          children: (
            <TableCNFollowPlanDetail
              year={record.year}
              loading={isGetTrackRevenueTeamSalesDetail}
              title={'Tiến độ'}
              data={profitRevenueProcess}
              ref={profitRevenueTableRef}
            />
          ),
          extra: <p>Đơn vị: triệu VND</p>,
        })}
      />
    </Flex>
  );
};

export default CNFollowPlanDetail;
